import { getCookie, trySetCookie } from '@packages/utils/cookies.ts'

import {
  META_BROWSER_ID_COOKIE,
  META_CLICK_ID_COOKIE,
  META_CLICK_ID_COOKIE_PATTERN,
  META_CLICK_ID_MAX_AGE_SECONDS,
  META_CLICK_ID_PATTERN,
  META_CLICK_ID_QUERY_PARAM,
} from '@layers/web/constants/tracking.ts'
import type { MetaMatchParameters } from '@layers/web/utils/types/tracking.ts'

const HOSTNAME_WITHOUT_REGISTRABLE_DOMAIN = /^(localhost|\d+(\.\d+){3}|\[.*\])$/

const getRegistrableCookieDomain = (hostname: string): string | undefined => {
  if (HOSTNAME_WITHOUT_REGISTRABLE_DOMAIN.test(hostname)) {
    return
  }

  return `.${hostname.split('.').slice(-2).join('.')}`
}

const getCookieSubdomainIndex = (cookieDomain: string): number =>
  cookieDomain.replace(/^\./, '').split('.').length - 1

const buildMetaClickIdCookieValue = (clickId: string, cookieDomain: string, createdAt: number): string =>
  `fb.${getCookieSubdomainIndex(cookieDomain)}.${createdAt}.${clickId}`

const readMetaClickIdFromUrl = (search: string): string | undefined => {
  const clickId = new URLSearchParams(search).get(META_CLICK_ID_QUERY_PARAM)

  if (!clickId || !META_CLICK_ID_PATTERN.test(clickId)) {
    return
  }

  return clickId
}

const getStoredMetaClickId = (): string | undefined =>
  getCookie(META_CLICK_ID_COOKIE)?.match(META_CLICK_ID_COOKIE_PATTERN)?.[1]

const persistMetaClickId = (clickId: string, hostname: string): void => {
  if (getStoredMetaClickId() === clickId) {
    return
  }

  const createdAt = Date.now()
  const registrableDomain = getRegistrableCookieDomain(hostname)

  const isWrittenOnRegistrableDomain = !!registrableDomain && trySetCookie(
    META_CLICK_ID_COOKIE,
    buildMetaClickIdCookieValue(clickId, registrableDomain, createdAt),
    {
      domain: registrableDomain,
      maxAgeSeconds: META_CLICK_ID_MAX_AGE_SECONDS,
    }
  )

  if (isWrittenOnRegistrableDomain) {
    return
  }

  trySetCookie(
    META_CLICK_ID_COOKIE,
    buildMetaClickIdCookieValue(clickId, hostname, createdAt),
    { maxAgeSeconds: META_CLICK_ID_MAX_AGE_SECONDS }
  )
}

const getMetaMatchParameters = (): MetaMatchParameters => {
  const fbc = getCookie(META_CLICK_ID_COOKIE)
  const fbp = getCookie(META_BROWSER_ID_COOKIE)

  return {
    ...(fbc && { fbc }),
    ...(fbp && { fbp }),
  }
}

export {
  getMetaMatchParameters,
  persistMetaClickId,
  readMetaClickIdFromUrl,
}
